import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { productAPI, favoriteAPI, publicAPI } from '@/services/api';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import ImageViewer from "@/components/ImageViewer";
import BackLink from "@/components/BackLink";
import LocationViewer from "@/components/LocationViewer";
import StickyInfoCard from "@/components/StickyInfoCard";

export default function ProductDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
	const [pricing, setPricing] = useState({
		free_images_limit: 5, price_extra_image: 0.70,
		free_videos_limit: 1, price_extra_video: 3.00,
		max_video_size_mb: 50, price_featured_ad: 10.00,
		vat_percent: 23
	});
	const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useDocumentMeta({
    title: product ? `${product.title} | Equi-Market` : 'Equi-Market',
    description: product?.description?.slice(0, 200),
    image: product?.images?.[0] ? (product.images[0].startsWith('http') ? product.images[0] : `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}`) : '',
    url: product ? `${window.location.origin}/products/${product.slug || product.id}` : '',
    price: product?.price,
    currency: 'EUR',
    type: 'product'
  });

  useEffect(() => {
    setLoading(true);
		publicAPI.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
    productAPI.get(id).then(r => {
      setProduct(r.data);
      if (user) {
				favoriteAPI.check(r.data.id).then(f => setIsFav(f.data.is_favorite))
					.catch(() => {});
			}
    }).catch(() => navigate('/products')).finally(() => setLoading(false));
  }, [id, user, navigate]);

	if (loading) return (
		<div className="pt-24 min-h-screen flex items-center justify-center">
			<div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin"/>
		</div>
	);

  if (!product) return null;

	const hiddenImages = product.hidden_images || [];
	const images = (product.images || []).filter(url => !hiddenImages.includes(url)).map((url, i) => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		thumbnail: product.thumbnails?.[i] ? (product.thumbnails[i].startsWith('/') ? `${ API_BASE }${ product.thumbnails[i] }` : product.thumbnails[i]) : null
	}));
	const hasLocation = product.location_lat && product.location_lng;

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				{/* Breadcrumb */}
				<BackLink title={product.title} uri="/products" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
						<ImageViewer images={images} title={product.title} is_featured={product.is_featured} />

						{/* Description */}
            <div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
              <h2 className="text-xl font-semibold text-[#1A3C34] mb-4 font-['Playfair_Display']">
								{t('horse.description')}
							</h2>
              <p className="text-[#666055] leading-relaxed whitespace-pre-wrap">{product.description}</p>
            </div>
						{/* Map */}
						{hasLocation &&
							<LocationViewer title={product.title}
															location_name={product.location_name}
															location_lat={product.location_lat}
															location_lng={product.location_lng} />
						}
					</div>

					{/* Right - Sticky Info */}
					<StickyInfoCard ad={product}
													adType="product"
													is_favorite={isFav}
													pricing={pricing} />
        </div>
      </div>
    </div>
  );
}
